import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Card,
    Avatar,
    Grid,
    Zoom,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    OutlinedInput,
    TextField,
    Button,
    Pagination,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";

// Import your images or use placeholders
import test from "../../assets/images/test.png";
import assessment from "../../assets/images/method.png";
import exam from "../../assets/images/exam.png";
import medal from "../../assets/images/medal.png";
import interviewToday from "../../assets/images/mental.png";
import first from "../../assets/images/first.png";
import second from "../../assets/images/second.png";
import third from "../../assets/images/third.png";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { TableNodata } from "../../components/TableNodata";
import { TopPerformersSkeleton, TimelineSkeleton } from "./DashboardCardSkeletons";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import Chart from "react-apexcharts";

const InterviewerDashboard = () => {

    const changeableItems = useChangeableItems();
    const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

    const [loaded, setLoaded] = useState(false);
    const currentMonth = dayjs().month();
    const currentYear = dayjs().year();

    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [testList, setTestList] = useState([]);
    const [topRank, setTopRank] = useState([]);
    const [listData, setListData] = useState([]);
    const [count, setCount] = useState(0);
    const [search, setSearch] = useState("");
    const [assessmentsDetail, setAssessmentsDetail] = useState([]);
    const [cardsLoading, setCardsLoading] = useState(true);
    const trainer = localStorage.getItem("role") === "TRAINER";
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];

    const years = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i);

    useEffect(() => {
        const timer = setTimeout(() => setLoaded(true), 200);
        return () => clearTimeout(timer);
    }, []);

    const commonAvatarStyle = {
        bgcolor: "#e8f2ff",
        color: "#1e88e5",
        width: 24,
        height: 24,
        fontWeight: "bold",
        border: "2px solid #bbdefb",
    };

    const avatarColors = [
        "#e53935",
        "#8e24aa",
        "#3949ab",
        "#00897b",
        "#f4511e",
        "#43a047",
        "#fdd835",
    ];

    const testData = async () => {
        setCardsLoading(true);
        try {
            const payload = {
                institute_id: +localStorage.getItem("InstituteId") || null,
                month: selectedMonth + 1 || null,
                year: selectedYear || null,
            };
            const res = await api.post("GetAllAssessmentsCountForTrainer", payload);
            setTestList(res?.data);
            setTopRank(res?.data?.top_performers || []);
            setAssessmentsDetail(res?.data?.assessment_info || []);
        } catch (error) {
            console.log("Error :", error);
            showSnackbar("error", "Fetching failed!");
        } finally {
            setCardsLoading(false);
        }
    };
    const studentAssessmentData = async () => {
        try {
            const payload = {
                page: page || 0,
                size: rowsPerPage || 100,
                search: search || "",
            };
            const res = await api.post("GetAllStudentAssessmentsForTrainer", payload);
            setListData(res?.data?.students || []);
            setCount(res?.data?.total_students || 0);
        } catch (error) {
            console.log("Error :", error);
            showSnackbar("error", "Fetching failed!");
        }
    };

    useEffect(() => {
        testData();
    }, [selectedMonth, selectedYear]);

    useEffect(() => {
        studentAssessmentData();
    }, [page, rowsPerPage, search]);

    // Check login status for conditional rendering
    const isLoggedIn = localStorage.getItem("logIn") !== "true";

    // Function to wrap content with Zoom conditionally
    const ConditionalZoom = ({ children, style, key }) => {
        if (isLoggedIn) {
            return (
                <Zoom in={loaded} style={style} key={key}>
                    {children}
                </Zoom>
            );
        }
        return children;
    };

    const interviewStatusData = {
        series: [12, 8, 5, 3],
        options: {
            chart: {
                type: "pie",
            },
            labels: [
                "Completed",
                "Pending",
                "Scheduled",
                "Cancelled",
            ],
            colors: [
                "#3B82F6",
                "#F59E0B",
                "#22C55E",
                "#F87171",
            ],
            legend: {
                show: false,
            },
            dataLabels: {
                enabled: true,
            },
            tooltip: {
                y: {
                    formatter: (value) =>
                        `${value} Interviews`,
                },
            },
        },
    };

    return (
        <Box>
            <Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Grid>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                            <DashboardIcon sx={{ color: "#1e88e5", fontSize: 32 }} />
                            <Typography
                                variant="h4"
                                fontWeight={700}
                                sx={{ color: "#0a0a0a" }}
                            >
                                Dashboard
                            </Typography>
                        </Box>
                        <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            Overall summary of your interview and schedule activities.
                        </Typography>
                    </Grid>
                </Box>

                {/* Cards Section */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "stretch",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    {/* Total Tests Card */}
                    <ConditionalZoom
                        in={loaded}
                        style={{ transitionDelay: "400ms" }}
                        key="total-tests-card"
                    >
                        <Card
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(40% - 16px)",
                                    md: "1 1 calc(21% - 16px)",
                                },
                                borderRadius: "8px",
                                border: "1px solid #c1c1c6ff",
                                background: "#fff",
                                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                p: 2,
                                transition: "all 0.3s ease",
                                // cursor: "pointer",
                                // "&:hover": {
                                //     transform: "translateY(-4px) scale(1.03)",
                                //     boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                                //     background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                                // },
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={600}
                                    sx={{ color: "#333", lineHeight: 1.1 }}
                                >
                                    Total no of Interviews
                                </Typography>
                                <Avatar sx={commonAvatarStyle}>
                                    <Box
                                        component="img"
                                        src={test}
                                        alt="Total Assessments"
                                        sx={{ width: 26, height: 26 }}
                                    />
                                </Avatar>
                            </Box>

                            <Typography
                                variant="h5"
                                fontWeight={700}
                                sx={{ color: "#1e88e5", mt: 1 }}
                            >
                                {27 || "0"}
                            </Typography>
                        </Card>
                    </ConditionalZoom>

                    {/* Self Assessments Card */}
                    <ConditionalZoom
                        in={loaded}
                        style={{ transitionDelay: "400ms" }}
                        key="self-assessments-card"
                    >
                        <Card
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(40% - 16px)",
                                    md: "1 1 calc(21% - 16px)",
                                },
                                borderRadius: "8px",
                                border: "1px solid #c1c1c6ff",
                                background: "#fff",
                                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                p: 2,
                                transition: "all 0.3s ease",
                                // cursor: "pointer",
                                // "&:hover": {
                                //     transform: "translateY(-4px) scale(1.03)",
                                //     boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                                //     background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                                // },
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={600}
                                    sx={{ color: "#333", lineHeight: 1.1 }}
                                >
                                    No of Pending Interviews
                                </Typography>
                                <Avatar sx={commonAvatarStyle}>
                                    <Box
                                        component="img"
                                        src={assessment}
                                        alt="Self Assessments"
                                        sx={{ width: 26, height: 26 }}
                                    />
                                </Avatar>
                            </Box>

                            <Typography
                                variant="h5"
                                fontWeight={700}
                                sx={{ color: "#1e88e5", mt: 1 }}
                            >
                                {11 || "0"}
                            </Typography>
                        </Card>
                    </ConditionalZoom>

                    {/* Proctored Tests Card */}
                    <ConditionalZoom
                        in={loaded}
                        style={{ transitionDelay: "400ms" }}
                        key="proctored-tests-card"
                    >
                        <Card
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(40% - 16px)",
                                    md: "1 1 calc(21% - 16px)",
                                },
                                borderRadius: "8px",
                                border: "1px solid #c1c1c6ff",
                                background: "#fff",
                                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                p: 2,
                                transition: "all 0.3s ease",
                                // cursor: "pointer",
                                // "&:hover": {
                                //     transform: "translateY(-4px) scale(1.03)",
                                //     boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                                //     background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                                // },
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={600}
                                    sx={{ color: "#333", lineHeight: 1.1 }}
                                >
                                    No of Completed Interviews
                                </Typography>
                                <Avatar sx={commonAvatarStyle}>
                                    <Box
                                        component="img"
                                        src={exam}
                                        alt="Proctored Tests"
                                        sx={{ width: 26, height: 26 }}
                                    />
                                </Avatar>
                            </Box>

                            <Typography
                                variant="h5"
                                fontWeight={700}
                                sx={{ color: "#1e88e5", mt: 1 }}
                            >
                                {16 || "0"}
                            </Typography>
                        </Card>
                    </ConditionalZoom>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "8px",
                        border: "1px solid #c1c1c6ff",
                        background: "#fff",
                        boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                        p: 2,
                    }}
                >
                    <ConditionalZoom
                        in={loaded}
                        style={{ transitionDelay: "400ms" }}
                        key="performance-overview-header"
                    >
                        <Grid
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mt: 1,
                            }}
                        >
                            <Grid
                                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                            >
                                <PermIdentityOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />
                                <Typography variant="h6" fontWeight={700}>
                                    {`${localStorage.getItem(
                                        "firstName",
                                    )}'s Interview Summary`}
                                </Typography>
                            </Grid>

                            <Grid>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Month</InputLabel>
                                            <Select
                                                value={selectedMonth}
                                                onChange={(e) => setSelectedMonth(e.target.value)}
                                                input={
                                                    <OutlinedInput
                                                        label="Month"
                                                        startAdornment={
                                                            <InputAdornment position="start">
                                                                <CalendarMonthIcon />
                                                            </InputAdornment>
                                                        }
                                                    />
                                                }
                                            >
                                                {months.map((month, index) => (
                                                    <MenuItem key={index} value={index}>
                                                        {month}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Year</InputLabel>
                                            <Select
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(e.target.value)}
                                                input={
                                                    <OutlinedInput
                                                        label="Year"
                                                        startAdornment={
                                                            <InputAdornment position="start">
                                                                <CalendarMonthIcon />
                                                            </InputAdornment>
                                                        }
                                                    />
                                                }
                                            >
                                                {years.map((year) => (
                                                    <MenuItem key={year} value={year}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </ConditionalZoom>

                    {/* Top Performers Section */}
                    <Box sx={{ display: "flex", gap: 3 }}>
                        {/* Top Performers Card */}
                        <ConditionalZoom
                            in={loaded}
                            style={{ transitionDelay: "800ms" }}
                            key="top-performers-card"
                        >
                            <Box sx={{ mt: 2, flex: 1 }}>
                                <Grid
                                    container
                                    direction="column"
                                    sx={{
                                        borderRadius: "8px",
                                        border: "1px solid #c1c1c6ff",
                                        background: "#fff",
                                        boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                                        p: 2,
                                    }}
                                >
                                    <Grid
                                        sx={{
                                            display: "flex",
                                            gap: 1,
                                            mb: 1,
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={interviewToday}
                                            alt="medal"
                                            sx={{ width: 30, height: 30 }}
                                        />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Interview Scheduled for Today
                                        </Typography>
                                    </Grid>
                                    {cardsLoading ? (
                                        <TopPerformersSkeleton />
                                    ) : topRank.length === 0 ? (
                                        <Grid
                                            sx={{
                                                overflowY: "auto",
                                                maxHeight: 350,
                                                minHeight: 350,
                                                pr: 1,
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                        >
                                            <TableNodata />
                                        </Grid>
                                    ) : (
                                        <Grid
                                            sx={{
                                                overflowY: "auto",
                                                maxHeight: 350,
                                                minHeight: 350,
                                                pr: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1.5,
                                            }}
                                        >
                                            {topRank.map((item, index) => (
                                                <Grid
                                                    container
                                                    alignItems="center"
                                                    sx={{
                                                        mb: 1,
                                                        p: 1,
                                                        borderRadius: 2,
                                                        // cursor: "pointer",
                                                        // "&:hover": { backgroundColor: "#f5f5f5" },
                                                    }}
                                                    key={`top-performer-${index}`}
                                                >
                                                    {/* Avatar + Name */}
                                                    <Grid
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            width: "35%",
                                                        }}
                                                    >
                                                        <Avatar sx={commonAvatarStyle}>
                                                            {item.student_name.charAt(0)}
                                                        </Avatar>
                                                        <Typography sx={{ fontWeight: 500 }}>
                                                            {item.student_name}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid sx={{ width: "50%" }}>
                                                        {/* Test Type */}
                                                        <Typography sx={{ textAlign: "left" }}>
                                                            {item.test_type}
                                                        </Typography>
                                                        <Grid>
                                                            <Typography
                                                                sx={{ fontSize: "12px", color: "#1e88e5" }}
                                                            >
                                                                {item?.category}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>

                                                    {/* Percentage + Medal */}
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "flex-end",
                                                            gap: 1,
                                                            width: "15%",
                                                        }}
                                                    >
                                                        <Typography sx={{ fontWeight: "bold" }}>
                                                            {item.final_score ? Math?.round(item.final_score) : 0}
                                                        </Typography>
                                                        {index === 0 && (
                                                            <Box
                                                                component="img"
                                                                src={first}
                                                                alt="First place"
                                                                sx={{ width: 26, height: 26 }}
                                                            />
                                                        )}
                                                        {index === 1 && (
                                                            <Box
                                                                component="img"
                                                                src={second}
                                                                alt="Second place"
                                                                sx={{ width: 26, height: 26 }}
                                                            />
                                                        )}
                                                        {index === 2 && (
                                                            <Box
                                                                component="img"
                                                                src={third}
                                                                alt="Third place"
                                                                sx={{ width: 26, height: 26 }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </ConditionalZoom>

                        {/* Assessment Info Section */}
                        <ConditionalZoom
                            in={loaded}
                            style={{ transitionDelay: "800ms" }}
                            key="assessment-timeline-card"
                        >
                            <Box sx={{ mt: 2, flex: 1 }}>
                                <Grid
                                    container
                                    direction="column"
                                    sx={{
                                        borderRadius: "8px",
                                        border: "1px solid #c1c1c6ff",
                                        background: "#fff",
                                        boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                                        p: 2,
                                    }}
                                >
                                    <Grid
                                        sx={{
                                            display: "flex",
                                            gap: 1,
                                            mb: 1,
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={assessment}
                                            alt="interview status"
                                            sx={{
                                                width: 30,
                                                height: 30,
                                            }}
                                        />

                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            Interview Status
                                        </Typography>
                                    </Grid>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "100%",
                                            minHeight: 350,
                                            gap: 5
                                        }}
                                    >
                                        {/* Pie Chart */}
                                        <Box
                                            sx={{
                                                flex: 1,
                                                display: "flex",
                                                justifyContent: "flex-end",
                                            }}
                                        >
                                            <Chart
                                                options={interviewStatusData.options}
                                                series={interviewStatusData.series}
                                                type="pie"
                                                width="100%"
                                                height={350}
                                            />
                                        </Box>

                                        {/* Legend */}
                                        <Box
                                            sx={{
                                                width: 150,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 2,
                                                pl: 2,
                                            }}
                                        >
                                            {interviewStatusData.options.labels.map(
                                                (label, index) => (
                                                    <Box
                                                        key={label}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 10,
                                                                height: 10,
                                                                borderRadius: "50%",
                                                                backgroundColor:
                                                                    interviewStatusData.options
                                                                        .colors[index],
                                                                flexShrink: 0,
                                                            }}
                                                        />

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {label}
                                                        </Typography>
                                                    </Box>
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                            </Box>
                        </ConditionalZoom>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default InterviewerDashboard;
